@if (loadError(); as message) {
    <div class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
        <span>{{ message }}</span>
        <button type="button" class="btn btn-sm btn-outline-danger" (click)="state.reload()">Prøv igen</button>
    </div>
}

@if (note(); as message) {
    <div class="alert alert-warning py-2" role="status">{{ message }}</div>
}

<div class="row g-3 mb-3">
    <!-- API / kø -->
    <div class="col-lg-4">
        <div class="card h-100">
            <div class="card-header d-flex justify-content-between align-items-center">
                <span class="fw-semibold">API og kø</span>
                <span class="badge rounded-pill" [class]="'badge rounded-pill ' + phaseBadgeClass()">{{ phaseLabel() }}</span>
            </div>
            <div class="card-body d-flex flex-column">
                <!-- The whole sync's state, judged from the API's queue and the workers' hubs. -->
                @if (syncPhase(); as sync) {
                    <p class="small mb-3">
                        @switch (sync.phase) {
                            @case ('running') { API'et tager imod, og alle tjenester arbejder. }
                            @case ('paused') { API'et parkerer nye kørsler i køen; tjenesterne arbejder det igangværende færdigt. }
                            @case ('stopping') { {{ sync.running }} i gang · {{ sync.closed }} af {{ sync.controllable }} tjenester stoppet. Ventende delkørsler bliver i deres køer. }
                            @case ('stopped') { Nye kørsler parkeres, ingen tjeneste tager arbejde ind, og intet er i gang. Skemaet kan deployes. }
                            @case ('partial') { {{ sync.closed }} af {{ sync.controllable }} tjenester er stoppet, men API'et tager stadig imod. Start sætter dem i gang igen. }
                            @default { Tilstanden kan ikke afgøres: kræver en AdminKey på tjenesterne og kontakt til deres status-hub. }
                        }
                    </p>
                }
                @if (pool(); as pool) {
                    @if (pool.error) {
                        <div class="text-danger small mb-3">{{ pool.error }}</div>
                    } @else {
                        @if (pool.note) {
                            <div class="text-secondary small mb-2">{{ pool.note }}</div>
                        }
                        <dl class="row mb-3 counts">
                            <dt class="col-7">I kø</dt>
                            <dd class="col-5 text-end tabular mb-0">{{ pool.queued }}</dd>
                            <dt class="col-7">Under behandling</dt>
                            <dd class="col-5 text-end tabular mb-0">{{ pool.processing }}</dd>
                            <dt class="col-7">På pause</dt>
                            <dd class="col-5 text-end tabular mb-0">{{ pool.paused }}</dd>
                        </dl>
                    }
                } @else {
                    <div class="text-secondary small mb-3">Henter …</div>
                }
                <div class="mt-auto d-flex justify-content-between align-items-end gap-2">
                    <div class="btn-group btn-group-sm" role="group" aria-label="Styring af synkroniseringen">
                        <button type="button" class="btn btn-warning" [disabled]="!canPause()" (click)="pause()"
                            title="API'et parkerer nye kørsler; tjenesterne arbejder videre">
                            <i class="fa-solid fa-pause me-1" aria-hidden="true"></i>Pause
                        </button>
                        <button type="button" class="btn btn-danger" [disabled]="!canStop()" (click)="stop()"
                            title="Pause, og alle tjenester lukker for nyt arbejde; det igangværende gøres færdigt">
                            <i class="fa-solid fa-stop me-1" aria-hidden="true"></i>Stop
                        </button>
                        <button type="button" class="btn btn-success" [disabled]="!canStart()" (click)="start()"
                            title="Tjenesterne åbner igen, og API'et slipper de parkerede kørsler">
                            <i class="fa-solid fa-play me-1" aria-hidden="true"></i>Start
                        </button>
                    </div>
                    <span class="text-secondary small text-truncate">{{ pool()?.name ?? '' }}</span>
                </div>
            </div>
        </div>
    </div>

    <!-- Tjenester -->
    <div class="col-lg-8">
        <div class="card h-100">
            <div class="card-header d-flex justify-content-between align-items-center">
                <span class="fw-semibold">Services</span>
                <span class="text-secondary small">
                    @if (environmentName(); as env) { {{ env }} · }
                    opdateret {{ generatedAt() | date:'HH:mm:ss' }}
                </span>
            </div>
            <div class="card-body">
                @if (services().length === 0) {
                    <div class="text-secondary small">Ingen tjenester fundet i opsætningen.</div>
                } @else {
                    <ul class="list-unstyled d-flex flex-wrap gap-2 mb-0">
                        @for (service of services(); track service.role) {
                            <li class="service border rounded px-2 py-1" [attr.data-reach]="service.reach" [attr.data-paused]="service.paused || null"
                                [title]="service.url ?? 'Ingen URL i AppConfiguration'">
                                <div class="d-flex align-items-center gap-2">
                                    <span class="reach-dot" aria-hidden="true"></span>
                                    <span class="fw-semibold">{{ service.role }}</span>
                                    <span class="visually-hidden">
                                        @switch (service.reach) {
                                            @case ('online') { {{ service.paused ? 'på pause' : 'online' }} }
                                            @case ('offline') { ingen kontakt }
                                            @default { ingen URL }
                                        }
                                    </span>
                                </div>
                                <div class="small text-secondary">
                                    @switch (service.reach) {
                                        @case ('online') {
                                            {{ service.statusLabel || '—' }}
                                            @if (service.processingCount !== null) {
                                                · {{ service.processingCount }} i gang
                                            }
                                        }
                                        @case ('offline') {
                                            Ingen kontakt
                                            @if (service.lastSeen) { siden {{ service.lastSeen | date:'HH:mm:ss' }} }
                                        }
                                        @default { Ingen URL }
                                    }
                                </div>
                            </li>
                        }
                    </ul>
                }
            </div>
        </div>
    </div>
</div>

<!-- Kørsler i gang -->
<h2 class="h6 mb-2">Kørsler i gang</h2>
<cfg-table [resource]="runsSource" [columns]="runColumns" [trackBy]="trackByRunId" height="30vh"
    emptyText="Ingen kørsler i gang." loadingText="Henter kørsler" errorText="Kørslerne kunne ikke hentes.">
    <ng-template cfgCell="status" [cfgCellOf]="state.value()?.runs" let-row>
        <span class="badge" [class]="'badge ' + (row.status | queueStatusClass)">{{ row.status | queueStatusLabel }}</span>
    </ng-template>
    <ng-template cfgCell="executers" [cfgCellOf]="state.value()?.runs" let-row>
        <ng-container *ngTemplateOutlet="executerCounts; context: { $implicit: row }" />
    </ng-template>
    <ng-template cfgCell="lastActivity" [cfgCellOf]="state.value()?.runs" let-row>
        <span class="tabular">{{ row.lastActivity | date:'dd-MM HH:mm:ss' }}</span>
        @if (staleFor(row)) {
            <span class="badge text-bg-warning ms-1" title="Over 10 minutter uden statusskrivning – QueueMaster genkører den snart">Strandet?</span>
        }
    </ng-template>
    <ng-template cfgCell="created" [cfgCellOf]="state.value()?.runs" let-row>
        <span class="tabular">{{ row.created | date:'dd-MM HH:mm' }}</span>
    </ng-template>
    <ng-template cfgCell="id" [cfgCellOf]="state.value()?.runs" let-row>
        <a class="btn btn-sm btn-outline-primary" [routerLink]="['/synchronizations/monitoring']"
            [queryParams]="{ queueId: row.id }" title="Åbn i Overvågning">Vis</a>
    </ng-template>
</cfg-table>

<!-- Køens rækker -->
<h2 class="h6 mt-4 mb-2">Kø</h2>
<cfg-table [resource]="poolSource" [columns]="poolColumns" [trackBy]="trackByQueueId" height="30vh"
    emptyText="Køen er tom." loadingText="Henter køen" errorText="Køen kunne ikke hentes.">
    <ng-template cfgCell="status" [cfgCellOf]="state.value()?.pool?.items" let-row>
        <span class="badge" [class]="'badge ' + rowStatusClass(row.status)">{{ rowStatusLabel(row.status) }}</span>
    </ng-template>
    <ng-template cfgCell="added" [cfgCellOf]="state.value()?.pool?.items" let-row>
        <span class="tabular">{{ row.added | date:'dd-MM HH:mm:ss' }}</span>
    </ng-template>
</cfg-table>

<!-- Strandede delkørsler -->
@if (hasStranded()) {
    <h2 class="h6 mt-4 mb-1">Afsluttede kørsler med uafsluttede delkørsler</h2>
    <p class="small text-secondary mb-2">
        Kørslen er afsluttet, men delkørsler står stadig som i gang. De tælles med i rollup og i release-workflowets drain-tjek.
    </p>
    <cfg-table [resource]="strandedSource" [columns]="runColumns" [trackBy]="trackByRunId" height="30vh"
        emptyText="Ingen." loadingText="Henter" errorText="Kunne ikke hentes.">
        <ng-template cfgCell="status" [cfgCellOf]="state.value()?.stranded" let-row>
            <span class="badge" [class]="'badge ' + (row.status | queueStatusClass)">{{ row.status | queueStatusLabel }}</span>
        </ng-template>
        <ng-template cfgCell="executers" [cfgCellOf]="state.value()?.stranded" let-row>
            <ng-container *ngTemplateOutlet="executerCounts; context: { $implicit: row }" />
        </ng-template>
        <ng-template cfgCell="lastActivity" [cfgCellOf]="state.value()?.stranded" let-row>
            <span class="tabular">{{ row.lastActivity | date:'dd-MM HH:mm:ss' }}</span>
        </ng-template>
        <ng-template cfgCell="created" [cfgCellOf]="state.value()?.stranded" let-row>
            <span class="tabular">{{ row.created | date:'dd-MM HH:mm' }}</span>
        </ng-template>
        <ng-template cfgCell="id" [cfgCellOf]="state.value()?.stranded" let-row>
            <a class="btn btn-sm btn-outline-primary" [routerLink]="['/synchronizations/monitoring']"
                [queryParams]="{ queueId: row.id }" title="Åbn i Overvågning">Vis</a>
        </ng-template>
    </cfg-table>
}

<!-- Delkørsler as done/total plus the non-zero in-flight and failed buckets. -->
<ng-template #executerCounts let-run>
    @if (run.executers.total === 0) {
        <span class="text-secondary">—</span>
    } @else {
        <span class="tabular">{{ run.executers.done }}/{{ run.executers.total }}</span>
        @if (run.executers.executing > 0) {
            <span class="badge text-bg-primary ms-1" title="Påbegyndt">{{ run.executers.executing }}</span>
        }
        @if (run.executers.waiting > 0) {
            <span class="badge text-bg-info ms-1" title="Venter">{{ run.executers.waiting }}</span>
        }
        @if (run.executers.postSynchronizationQueued > 0) {
            <span class="badge text-bg-secondary ms-1" title="Post-synk i kø">{{ run.executers.postSynchronizationQueued }}</span>
        }
        @if (run.executers.failed > 0) {
            <span class="badge text-bg-danger ms-1" title="Fejlet">{{ run.executers.failed }}</span>
        }
    }
</ng-template>
